/**
 * useSmartAddCallables Hook
 *
 * Provides callable function wrappers for SmartAdd operations.
 */

import { useMutation } from '@tanstack/react-query';
import { httpsCallable } from 'firebase/functions';
import { functions } from '@/lib/firebase';
import type {
  AnalyzePurchaseInvoiceRequest,
  AnalyzePurchaseInvoiceResponse,
  CommitPurchaseInvoiceRequest,
  CommitPurchaseInvoiceResponse,
} from '../types';

// ============================================================================
// V2 Types (Strict - No Optional Money Fields)
// ============================================================================

// Supplier discriminated union types
type SupplierExisting = {
  mode: 'existing';
  existingCustomerId: string;
};

type SupplierNew = {
  mode: 'new';
  draft: {
    name: string;
    email?: string;
    phone?: string;
    vatNumber?: string;
    iban?: string;
    address?: {
      line1?: string;
      postalCode?: string;
      city?: string;
      country?: string;
    };
  };
};

type SupplierPayload = SupplierExisting | SupplierNew;

export interface CommitPurchaseInvoiceRequestV2 {
  teamId: string;
  uploadId: string;
  idempotencyKey: string;
  purchaseInvoice: {
    invoiceNumber: string;
    invoiceDate: string; // ISO
    totalExVatCents: number; // MUST be int, no || 0 fallback
    vatCents: number; // MUST be int, no || 0 fallback
    totalIncVatCents: number; // MUST be int, no || 0 fallback
    notes: string | null; // Explicit
  };
  supplier: SupplierPayload;
  vehicles?: any[]; // Vehicle drafts from extraction
  link: {
    attachSourcePdf: boolean;
  };
}

export interface CommitPurchaseInvoiceResponseV2 {
  success: boolean;
  error?: string;
  errorCode?: string;
  purchaseInvoiceId?: string;
  customerId?: string;
  vehicleIds?: string[];
  // Echo persisted values
  subtotalCents?: number;
  vatCents?: number;
  totalCents?: number;
  bpmCents?: number;
  notes?: string | null;
}

// ============================================================================
// Callables
// ============================================================================

// ============================================================================
// Initiate/Cancel Types
// ============================================================================

export interface InitiateSmartAddRequest {
  teamId: string;
  uploadId: string;
  fileName: string;
}

export interface InitiateSmartAddResponse {
  success: boolean;
  uploadId?: string;
  status?: string;
  error?: string;
  errorCode?: string;
}

export interface CancelSmartAddRequest {
  teamId: string;
  uploadId: string;
}

export interface CancelSmartAddResponse {
  success: boolean;
  status?: string;
  error?: string;
  errorCode?: string;
}

// ============================================================================
// Callables
// ============================================================================

const initiateCallable = httpsCallable<InitiateSmartAddRequest, InitiateSmartAddResponse>(
  functions,
  'smartAddInitiate'
);

const analyzeCallable = httpsCallable<
  AnalyzePurchaseInvoiceRequest,
  AnalyzePurchaseInvoiceResponse
>(functions, 'smartAddAnalyzePurchaseInvoice');

const commitCallable = httpsCallable<CommitPurchaseInvoiceRequest, CommitPurchaseInvoiceResponse>(
  functions,
  'smartAddCommitPurchaseInvoice'
);

const commitV2Callable = httpsCallable<
  CommitPurchaseInvoiceRequestV2,
  CommitPurchaseInvoiceResponseV2
>(functions, 'smartAddCommitPurchaseInvoiceV2');

const cancelCallable = httpsCallable<CancelSmartAddRequest, CancelSmartAddResponse>(
  functions,
  'smartAddCancel'
);

/**
 * Hook to initiate a SmartAdd job (creates job doc before file upload)
 */
export function useInitiateSmartAdd() {
  return useMutation({
    mutationFn: async (request: InitiateSmartAddRequest) => {
      const result = await initiateCallable(request);
      return result.data;
    },
  });
}

export function useAnalyzePurchaseInvoice() {
  return useMutation({
    mutationFn: async (request: AnalyzePurchaseInvoiceRequest) => {
      const result = await analyzeCallable(request);
      return result.data;
    },
  });
}

export function useCommitPurchaseInvoice() {
  return useMutation({
    mutationFn: async (request: CommitPurchaseInvoiceRequest) => {
      const result = await commitCallable(request);
      return result.data;
    },
  });
}

export function useCommitPurchaseInvoiceV2() {
  return useMutation({
    mutationFn: async (request: CommitPurchaseInvoiceRequestV2) => {
      const result = await commitV2Callable(request);
      return result.data;
    },
  });
}

/**
 * Hook to cancel a SmartAdd job
 */
export function useCancelSmartAdd() {
  return useMutation({
    mutationFn: async (request: CancelSmartAddRequest) => {
      const result = await cancelCallable(request);
      return result.data;
    },
  });
}
